import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { awardService } from '../../services/awardService';
import { Heart, Calendar, Trophy, Crown, Star, Users, Map } from 'lucide-react';
import { PublicHeader } from '../../components/layout/PublicHeader';
import { PublicFooter } from '../../components/layout/PublicFooter';
import { Award } from '../../types/award';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
import { TopFiveGlobal } from '../../components/shared/TopFiveGlobal';

// Styles pour l'hexagone
const clipPathHexagon = {
  clipPath: 'polygon(50% 0%, 93% 25%, 93% 75%, 50% 100%, 7% 75%, 7% 25%)'
};

export const AwardsPage: React.FC = () => {
  const [awards, setAwards] = useState<Award[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchAwards = async () => {
      try {
        const data = await awardService.getAllAwards();
        setAwards(data);
      } catch (error) {
        console.error('Erreur lors de la récupération des awards:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAwards();
  }, []);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  if (isLoading) {
    return (
      <>
        <PublicHeader />
        <div className="min-h-screen bg-[#111216] flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400"></div>
        </div>
      </>
    );
  }

  return (
    <>
      <PublicHeader />
      
      <div className="bg-[#111216] text-white relative overflow-hidden pt-20">
        {/* SEO Meta Tags */}
        <SEOMetaTags
          title="Cérémonies de Récompense en Afrique | VotAfrica"
          description="Découvrez les cérémonies de récompense les plus prestigieuses d'Afrique. Votez pour vos artistes, créateurs et personnalités préférés et célébrez l'excellence africaine sur VotAfrica."
          image="https://votafrica.com/awards-showcase.jpg"
          url="https://votafrica.com/awards"
        />
        
        {/* Background Image avec Overlay et Parallaxe */}
        <div 
          className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
          style={{
            backgroundImage: 'url(https://images.rawpixel.com/image_800/cHJpdmF0ZS9sci9pbWFnZXMvd2Vic2l0ZS8yMDI0LTA4L3N0YXJ0dXBpbWFnZXNfZ29sZF9nbGl0dGVyX2dvbGRfY29uZmV0dGlfYm9rZWhfc3BhcmtsZV9zaW1wbGVfZ185MTEzYzJkMy03YjEyLTQwYmQtYjNhYS04OTVlOGU4MmM5N2NfMi5qcGc.jpg)',
            transform: 'translateZ(0)',
            willChange: 'transform',
            backgroundAttachment: 'fixed'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/95 via-black/80 to-black/95 backdrop-blur-[2px]"></div>
        </div>

        {/* Hero Section */}
        <div className="relative min-h-[70vh] overflow-hidden flex items-center">
          {/* Contenu du Hero */}
          <div className="relative z-20 container mx-auto px-4">
            <motion.div 
              className="max-w-4xl mx-auto text-center"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {/* Logo décoratif */}
              <div className="mb-8 relative">
                <Crown className="w-16 h-16 mx-auto text-yellow-400 mb-4" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-yellow-400/20 rounded-full blur-2xl" />
              </div>

              {/* Titre principal */}
              <h1 className="relative mb-6">
                <span className="
                  text-6xl md:text-7xl font-playfair font-bold
                  bg-gradient-to-r from-yellow-200 via-yellow-400 to-yellow-200
                  bg-clip-text text-transparent
                  tracking-wide
                ">
                  VotAfrica Awards
                </span>
                {/* Ornements du titre */}
                <div className="absolute -left-4 -right-4 top-1/2 -translate-y-1/2 -z-10">
                  <div className="h-[1px] bg-gradient-to-r from-transparent via-yellow-400/30 to-transparent" />
                </div>
              </h1>

              {/* Description élégante */}
              <p className="
                text-xl md:text-2xl font-playfair text-white/90
                leading-relaxed tracking-wide mb-12
                max-w-2xl mx-auto
              ">
                Découvrez et célébrez l'excellence à travers une sélection prestigieuse de talents africains
              </p>

              {/* En-tête avec statistiques */}
              <div className="text-center max-w-4xl mx-auto px-4 mb-2">
                {/* Nouvelle barre de stats */}
                <div className="
                  flex flex-col sm:flex-row items-center justify-center
                  gap-4 sm:gap-12
                  px-4 sm:px-12 py-4 sm:py-6
                  border-t border-b border-yellow-400/20
                  backdrop-blur-sm bg-black/20
                  w-full sm:w-auto
                ">
                  <div className="flex items-center gap-3">
                    <Users className="w-5 sm:w-6 h-5 sm:h-6 text-yellow-400" />
                    <span className="font-playfair tracking-wider text-sm sm:text-base">
                      +1000 Participants
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Heart className="w-5 sm:w-6 h-5 sm:h-6 text-yellow-400" />
                    <span className="font-playfair tracking-wider text-sm sm:text-base">
                      +50K Votes
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Map className="w-5 sm:w-6 h-5 sm:h-6 text-yellow-400" />
                    <span className="font-playfair tracking-wider text-sm sm:text-base">
                      +20 Pays
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Awards Grid - Nouvelle présentation spectaculaire */}
        <div className="relative pt-0 pb-8 flex items-center justify-center px-4 md:px-8">
          <motion.div 
            className="relative w-full max-w-[1400px]"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Ligne décorative horizontale */}
            <div className="absolute top-1/2 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-yellow-400/20 to-transparent" />
            
            {/* Lignes rayonnantes */}
            <div className="absolute inset-0 hidden md:block opacity-20">
              {Array.from({ length: 24 }).map((_, i) => (
                <div
                  key={i}
                  className="absolute inset-0 origin-center"
                  style={{
                    transform: `rotate(${i * 15}deg)`,
                  }}
                >
                  <div className="absolute top-0 left-1/2 h-full w-[1px] bg-gradient-to-b from-transparent via-yellow-400/10 to-transparent transform -translate-x-1/2" />
                </div>
              ))}
            </div>

            {/* Awards en ligne horizontale */}
            <div className="
              relative
              flex flex-col md:flex-row items-center justify-center
              gap-8 md:gap-16
              pt-4
            ">
              {/* Cercle Awards */}
              <div className="
                order-first md:order-2
                w-32 h-32 md:w-40 md:h-40
                bg-gradient-to-br from-[#1a1a1a] to-[#2a2a2a]
                rounded-full
                backdrop-blur-lg
                flex items-center justify-center
                border border-yellow-400/30
                shadow-xl shadow-black/50
                animate-pulse
                relative z-10
              ">
                <div className="text-center">
                  <Trophy className="w-8 h-8 md:w-12 md:h-12 mx-auto text-yellow-400 mb-2" />
                  <span className="text-base md:text-lg font-playfair text-white/90">
                    {awards.length} Awards
                  </span>
                </div>
              </div>

              {/* Cercle Catégories */}
              <div className="
                order-2 md:order-3
                w-32 h-32 md:w-40 md:h-40
                bg-gradient-to-br from-[#1a1a1a] to-[#2a2a2a]
                rounded-full
                backdrop-blur-lg
                flex items-center justify-center
                border border-yellow-400/30
                shadow-xl shadow-black/50
                animate-pulse
                relative z-10
              ">
                <div className="text-center">
                  <Star className="w-8 h-8 md:w-12 md:h-12 mx-auto text-yellow-400 mb-2" />
                  <span className="text-base md:text-lg font-playfair text-white/90">
                    {awards.reduce((total, award) => total + (award.award_categories?.length || 0), 0)} Catégories
                  </span>
                </div>
              </div>

              {/* Awards */}
              {awards.map((award, index) => (
                <motion.div
                  key={award.id}
                  variants={cardVariants}
                  className={`relative w-full max-w-[320px] ${
                    index === 0 ? 'order-0 md:order-1' : 'order-3 md:order-4'
                  }`}
                >
                  <Link
                    to={`/awards/${award.slug}`}
                    className="
                      group relative
                      aspect-square
                      flex items-center justify-center
                      hover:scale-110 transition-transform duration-500
                    "
                  >
                    {/* Hexagone de fond avec effet de brillance */}
                    <div
                      style={clipPathHexagon}
                      className="
                        absolute inset-0
                        bg-gradient-to-br from-[#1a1a1a] via-[#272727] to-[#2a2a2a]
                        backdrop-blur-sm
                        transition-all duration-500
                        group-hover:from-[#2a2a2a] group-hover:via-[#1f1f1f] group-hover:to-[#3a3a3a]
                        border border-yellow-400/20
                        group-hover:border-yellow-400/40
                        ring-1 ring-yellow-400/10
                        hover:ring-2 hover:ring-yellow-400/30
                        shadow-[0_4px_30px_rgba(245,158,11,0.3)]
                        group-hover:shadow-[0_0_50px_rgba(245,158,11,0.6)]
                      "
                    >
                      {/* Points décoratifs */}
                      <motion.div
                        className="absolute top-4 left-1/2 -translate-x-1/2 w-3 h-3 text-yellow-400"
                        animate={{ rotate: [0, 360], scale: [0.9, 1.1] }}
                        transition={{ duration: 4, repeat: Infinity }}
                      >
                        <Star className="w-full h-full fill-current" />
                      </motion.div>
                      <motion.div
                        className="absolute -top-1 left-1/2 -translate-x-1/2 w-4 h-4 text-yellow-500"
                        animate={{ opacity: [0.7, 1, 0.7] }}
                        transition={{ duration: 3, repeat: Infinity }}
                      >
                        <Star className="w-full h-full fill-current" />
                      </motion.div>
                      <motion.div
                        className="absolute top-1/4 right-[7%] w-3 h-3 text-yellow-400"
                        animate={{ rotate: [0, 180, 0] }}
                        transition={{ duration: 5, repeat: Infinity }}
                      >
                        <Star className="w-full h-full fill-current" />
                      </motion.div>
                      <motion.div
                        className="absolute bottom-1/4 right-[7%] w-3 h-3 text-yellow-400"
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 3, repeat: Infinity }}
                      >
                        <Star className="w-full h-full fill-current" />
                      </motion.div>
                      <motion.div
                        className="absolute top-1/4 left-[7%] w-3 h-3 text-yellow-400"
                        animate={{ y: [0, -3, 0] }}
                        transition={{ duration: 4, repeat: Infinity }}
                      >
                        <Star className="w-full h-full fill-current" />
                      </motion.div>
                      <motion.div
                        className="absolute bottom-1/4 left-[7%] w-3 h-3 text-yellow-400"
                        animate={{ rotate: [0, -180, 0] }}
                        transition={{ duration: 6, repeat: Infinity }}
                      >
                        <Star className="w-full h-full fill-current" />
                      </motion.div>
                      {/* Pépites d'or */}
                      <motion.div 
                        className="absolute top-8 left-[20%] w-1.5 h-1.5 bg-yellow-300 rounded-full shadow-[0_0_5px_rgba(245,158,11,0.5)]"
                        animate={{ opacity: [0.8, 1, 0.8] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      />
                      <motion.div 
                        className="absolute bottom-6 right-[25%] w-2 h-2 bg-yellow-500 rounded-full shadow-[0_0_8px_rgba(245,158,11,0.7)]"
                        animate={{ scale: [1, 1.1, 1] }}
                        transition={{ duration: 3, repeat: Infinity }}
                      />
                      <motion.div 
                        className="absolute top-[15%] left-[15%] w-1 h-1 bg-yellow-400 rounded-full shadow-[0_0_3px_rgba(245,158,11,0.3)]"
                        animate={{ y: [-2, 2, -2] }}
                        transition={{ duration: 4, repeat: Infinity }}
                      />
                      <motion.div 
                        className="absolute bottom-[30%] right-[15%] w-1.5 h-1.5 bg-yellow-400 rounded-full shadow-[0_0_5px_rgba(245,158,11,0.5)]"
                        animate={{ opacity: [0.6, 1, 0.6] }}
                        transition={{ duration: 2.5, repeat: Infinity }}
                      />
                      {/* Diamants */}
                      <motion.div 
                        className="absolute top-[25%] right-6 w-3 h-3 bg-white/70 transform rotate-45 shadow-[0_0_4px_rgba(255,255,255,0.7)]"
                        animate={{ rotate: [45, 50, 45] }}
                        transition={{ duration: 3, repeat: Infinity }}
                      />
                      <motion.div 
                        className="absolute bottom-[20%] left-8 w-3 h-3 bg-white/50 transform rotate-45 shadow-[0_0_4px_rgba(255,255,255,0.5)]"
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      />
                      <motion.div 
                        className="absolute top-[40%] left-[10%] w-2 h-2 bg-white/60 transform rotate-45 shadow-[0_0_3px_rgba(255,255,255,0.6)]"
                        animate={{ opacity: [0.7, 1, 0.7] }}
                        transition={{ duration: 2.2, repeat: Infinity }}
                      />
                      <motion.div 
                        className="absolute bottom-[40%] right-[10%] w-2.5 h-2.5 bg-white/80 transform rotate-45 shadow-[0_0_5px_rgba(255,255,255,0.8)]"
                        animate={{ y: [0, -3, 0] }}
                        transition={{ duration: 3.5, repeat: Infinity }}
                      />
                      {/* Étoiles dorées */}
                      <motion.div 
                        className="absolute top-[5%] left-[10%] w-2 h-2 text-yellow-400"
                        animate={{ rotate: [0, 360], scale: [0.8, 1.2] }}
                        transition={{ duration: 5, repeat: Infinity }}
                      >
                        <Star className="w-full h-full fill-current" />
                      </motion.div>
                      <motion.div 
                        className="absolute top-[10%] right-[15%] w-3 h-3 text-yellow-300"
                        animate={{ opacity: [0.5, 1, 0.5] }}
                        transition={{ duration: 3, repeat: Infinity }}
                      >
                        <Star className="w-full h-full fill-current" />
                      </motion.div>
                      <motion.div 
                        className="absolute bottom-[5%] left-[15%] w-1.5 h-1.5 text-yellow-500"
                        animate={{ y: [0, -3, 0] }}
                        transition={{ duration: 4, repeat: Infinity }}
                      >
                        <Star className="w-full h-full fill-current" />
                      </motion.div>
                      <motion.div 
                        className="absolute bottom-[10%] right-[10%] w-2.5 h-2.5 text-yellow-400"
                        animate={{ scale: [1, 1.3, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      >
                        <Star className="w-full h-full fill-current" />
                      </motion.div>
                      <motion.div 
                        className="absolute top-[30%] left-[5%] w-1 h-1 text-yellow-300"
                        animate={{ rotate: [0, 180, 0] }}
                        transition={{ duration: 6, repeat: Infinity }}
                      >
                        <Star className="w-full h-full fill-current" />
                      </motion.div>
                      <motion.div 
                        className="absolute top-[35%] right-[5%] w-1.8 h-1.8 text-yellow-500"
                        animate={{ opacity: [0.7, 1, 0.7] }}
                        transition={{ duration: 2.5, repeat: Infinity }}
                      >
                        <Star className="w-full h-full fill-current" />
                      </motion.div>
                      <motion.div 
                        className="absolute bottom-[30%] left-[3%] w-1.2 h-1.2 text-yellow-400"
                        animate={{ y: [0, 2, 0] }}
                        transition={{ duration: 3.5, repeat: Infinity }}
                      >
                        <Star className="w-full h-full fill-current" />
                      </motion.div>
                      <motion.div 
                        className="absolute bottom-[35%] right-[3%] w-1.5 h-1.5 text-yellow-300"
                        animate={{ rotate: [0, -180, 0] }}
                        transition={{ duration: 5.5, repeat: Infinity }}
                      >
                        <Star className="w-full h-full fill-current" />
                      </motion.div>
                      <motion.div 
                        className="absolute top-[50%] left-[8%] w-1.3 h-1.3 text-yellow-500"
                        animate={{ scale: [1, 1.5, 1] }}
                        transition={{ duration: 3, repeat: Infinity }}
                      >
                        <Star className="w-full h-full fill-current" />
                      </motion.div>
                      <motion.div 
                        className="absolute top-[55%] right-[8%] w-1.7 h-1.7 text-yellow-400"
                        animate={{ opacity: [0.6, 1, 0.6] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      >
                        <Star className="w-full h-full fill-current" />
                      </motion.div>
                    </div>

                    {/* Contenu */}
                    <div className="relative z-10 p-10 text-center w-[85%] mx-auto">
                      <Crown className="w-8 h-8 mx-auto text-yellow-400 mb-4" />
                      <h3 className="text-lg font-playfair font-semibold mb-3 text-white px-2">
                        {award.name}
                      </h3>
                      <div className="
                        inline-flex items-center gap-2 mb-4
                        px-3 py-1
                        bg-black/30 rounded-full
                        text-xs text-yellow-400
                      ">
                        <Star className="w-3 h-3" />
                        {award.award_categories?.length || 0} catégories
                      </div>
                      <p className="text-xs text-white/60 line-clamp-2 px-2">
                        {award.description.length > 50 ? award.description.substring(0, 50) + '...' : award.description}
                      </p>
                    </div>

                    {/* Points lumineux autour de l'hexagone */}
                    <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-yellow-400 rounded-full" />
                    <div className="absolute top-[15%] -right-1 w-1 h-1 bg-yellow-400 rounded-full" />
                    <div className="absolute bottom-[15%] -right-1 w-1 h-1 bg-yellow-400 rounded-full" />
                    <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-yellow-400 rounded-full" />
                    <div className="absolute bottom-[15%] -left-1 w-1 h-1 bg-yellow-400 rounded-full" />
                    <div className="absolute top-[15%] -left-1 w-1 h-1 bg-yellow-400 rounded-full" />
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Top 5 Global */}
      <div className="py-16 bg-black/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-white/5 backdrop-blur-sm rounded-lg border border-white/10 p-6">
            <TopFiveGlobal />
          </div>
        </div>
      </div>

      <PublicFooter />
    </>
  );
};
